import { useEffect, useRef, useState } from 'react'
import {
  useAuth,
  useOrganization,
  useUser,
  OrganizationList,
  OrganizationSwitcher,
  SignedIn,
  SignedOut,
  SignIn,
  SignUp,
  UserButton,
} from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import {
  BrowserRouter,
  Link,
  Outlet,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom'
import { Menu, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { AccessGate } from '@/components/SignUpGate'
import { EricOnlineDot } from '@/components/dashboard/agentic/EricOnlineDot'
import { ProductionTenantBanner } from '@/components/ProductionTenantBanner'
import { WhatsNewDialog } from '@/components/WhatsNewDialog'
import { AppShell } from '@/components/shell/AppShell'
import More from '@/pages/More'
import { Activity } from '@/pages/Activity'
import { Appointments } from '@/pages/Appointments'
import { Calls } from '@/pages/Calls'
import { Customers } from '@/pages/Customers'
import { CustomerDetail } from '@/pages/CustomerDetail'
import { Dealers } from '@/pages/Dealers'
import { EstimateDetail } from '@/pages/EstimateDetail'
import { Estimates } from '@/pages/Estimates'
import { Messaging } from '@/pages/Messaging'
import { ThreadsArchive } from '@/pages/Threads'
import { Today } from '@/pages/Today'
import { NewCustomer } from '@/pages/NewCustomer'
import { NewEstimate } from '@/pages/NewEstimate'
import { Settings } from '@/pages/Settings'
import { Suppliers } from '@/pages/Suppliers'
import { Vehicles } from '@/pages/Vehicles'
import { VehicleDetail } from '@/pages/VehicleDetail'
import { VehicleFromPhoto } from '@/pages/VehicleFromPhoto'
import { AdminActivity } from '@/pages/admin/Activity'
import { AdminAlerts } from '@/pages/admin/Alerts'
import { AdminAuditLog } from '@/pages/admin/AuditLog'
import { AdminEricActivity } from '@/pages/admin/EricActivity'
import { AdminIntegrations } from '@/pages/admin/Integrations'
import { AdminNewTenant } from '@/pages/admin/NewTenant'
import { AdminTenantDetail } from '@/pages/admin/TenantDetail'
import { AdminTenants } from '@/pages/admin/Tenants'
import { AdminUsage } from '@/pages/admin/Usage'
import { AdminUsageDetail } from '@/pages/admin/UsageDetail'
import { identifyShopUser, resetIdentity, track } from '@/lib/analytics'
import { useCanManageIntegrations, useIsPlatformAdmin } from '@/lib/auth'
import { pageNameFor } from '@/lib/page-name'

// Two distinct deployments share this Worker:
//
//   - **Tenant app** at `staging.autoflo.app` — what shop owners,
//     mechanics, and receptionists see. Full app shell with all
//     business surfaces. Signed-out flow is gated behind a 6-digit
//     access code while we're in invite-only beta.
//
//   - **Platform-admin portal** at `admin.autoflo.app` — Mira/Emil-only
//     internal tool. Same worker, same Clerk auth, same DB, but a
//     stripped routing tree (admin pages only, no tenant surfaces).
//     No access code (internal users), platform-admin gate enforced
//     client- and server-side.
//
// `isAdminHost` is resolved at module load (the hostname doesn't
// change mid-session). Both branches still go through Clerk's
// SignedOut/SignedIn split and the OrgGate — only the route tree
// inside differs.
//
// Dev convenience: on `npm run dev` (Vite serves only on localhost,
// can't reach admin.localhost without extra DNS config), also treat
// any URL whose path starts with `/admin/` as the admin shell. Gated
// to `import.meta.env.DEV` so the production build's `admin.` host
// gate is the only entry point in prod. The server-side
// `requirePlatformAdmin()` check on every /api/admin/* route is the
// real authorization boundary either way — flipping the client shell
// can't reach data the JWT isn't allowed to read.
const isAdminHost =
  typeof window !== 'undefined' &&
  (window.location.hostname.startsWith('admin.') ||
    (import.meta.env.DEV &&
      window.location.pathname.startsWith('/admin/')))

// Tenant nav links carry an i18next key rather than a literal label so
// each link's display text resolves through `t(labelKey)` at render
// time. Use colon-namespace syntax so the resolution works no matter
// which namespace `useTranslation()` was called with (NavList uses
// default).
//
// IMPORTANT: there is no `/admin` link here even for platform admins.
// Mira/Emil go to admin.autoflo.app for admin work; the tenant app
// stays purely tenant-shaped.
const TENANT_NAV_LINKS: { to: string; labelKey: string }[] = [
  { to: '/dashboard', labelKey: 'nav:dashboard' },
  { to: '/estimates', labelKey: 'nav:estimates' },
  { to: '/appointments', labelKey: 'nav:appointments' },
  { to: '/calls', labelKey: 'nav:calls' },
  { to: '/customers', labelKey: 'nav:customers' },
  { to: '/vehicles', labelKey: 'nav:vehicles' },
  { to: '/dealers', labelKey: 'nav:dealers' },
  { to: '/suppliers', labelKey: 'nav:suppliers' },
  { to: '/messaging', labelKey: 'nav:messaging' },
  { to: '/threads', labelKey: 'nav:threads' },
  { to: '/settings', labelKey: 'nav:settings' },
]

const ADMIN_NAV_LINKS: { to: string; labelKey: string }[] = [
  { to: '/', labelKey: 'admin:nav.tenants' },
  { to: '/usage', labelKey: 'admin:nav.usage' },
  { to: '/eric-activity', labelKey: 'admin:nav.eric_activity' },
  { to: '/activity', labelKey: 'admin:nav.activity' },
  { to: '/alerts', labelKey: 'admin:nav.alerts' },
  { to: '/integrations', labelKey: 'admin:nav.integrations' },
  { to: '/audit', labelKey: 'admin:nav.audit' },
]

function useNavLinks() {
  return isAdminHost ? ADMIN_NAV_LINKS : TENANT_NAV_LINKS
}

function App() {
  return (
    <BrowserRouter>
      <SignedOut>
        {isAdminHost ? (
          // Admin host: internal tool, no access code. Sign-in only;
          // sign-up is not exposed here. If someone reaches admin.autoflo.app
          // without an account, they hit Clerk's SignIn (which on a dev
          // instance also offers Google/magic-link to create one — but
          // they still need platform_admin to get past the gate).
          <Routes>
            <Route
              path="*"
              element={
                <div className="min-h-dvh grid place-items-center bg-background px-4 py-8">
                  <SignIn />
                </div>
              }
            />
          </Routes>
        ) : (
          // Tenant host: ALL signed-out routes sit behind the 6-digit
          // access-code gate. Previously only /sign-up did, which let
          // anyone landing on / see Clerk's SignIn directly.
          <AccessGate>
            <Routes>
              <Route
                path="/sign-up"
                element={
                  <div className="min-h-dvh grid place-items-center bg-background px-4 py-8">
                    <SignUp />
                  </div>
                }
              />
              <Route
                path="*"
                element={
                  <div className="min-h-dvh grid place-items-center bg-background px-4 py-8">
                    <SignIn />
                  </div>
                }
              />
            </Routes>
          </AccessGate>
        )}
      </SignedOut>

      <SignedIn>
        <OrgGate>
          {isAdminHost ? <AdminShellRoutes /> : <TenantShellRoutes />}
        </OrgGate>
      </SignedIn>
    </BrowserRouter>
  )
}

export default App

/**
 * Tenant-side route tree. Everything a shop owner / mechanic /
 * receptionist can reach lives here. Notably, `/admin` is NOT in this
 * tree — platform-admin work happens on admin.autoflo.app.
 */
function TenantShellRoutes() {
  return (
    <Routes>
      {/* Tenant host renders the 5-tab AppShell (DESIGN.md §5).
          AdminLayout below keeps serving the admin host unchanged. */}
      <Route element={<AppShell />}>
        {/* PR-5: Today replaces the Dashboard component at the tenant
            home (DESIGN.md §5). HomeV2/AgenticDashboard stay in the
            tree unrouted until the cleanup PR deletes them. */}
        <Route path="/" element={<Today />} />
        <Route path="/dashboard" element={<Today />} />
        <Route path="/more" element={<More />} />
        <Route path="/dealers" element={<Dealers />} />
        <Route path="/estimates" element={<Estimates />} />
        <Route path="/estimates/new" element={<NewEstimate />} />
        <Route path="/estimates/:id" element={<EstimateDetail />} />
        <Route path="/appointments" element={<Appointments />} />
        <Route path="/calls" element={<Calls />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/customers/new" element={<NewCustomer />} />
        <Route path="/customers/:id" element={<CustomerDetail />} />
        <Route path="/vehicles" element={<Vehicles />} />
        <Route path="/vehicles/from-photo" element={<VehicleFromPhoto />} />
        <Route path="/vehicles/:id" element={<VehicleDetail />} />
        <Route path="/suppliers" element={<Suppliers />} />
        <Route path="/messaging" element={<Messaging />} />
        <Route path="/threads" element={<ThreadsArchive />} />
        <Route path="/activity" element={<Activity />} />
        <Route
          path="/settings"
          element={
            <RequireOwner>
              <Settings />
            </RequireOwner>
          }
        />
      </Route>
    </Routes>
  )
}

/**
 * Admin-side route tree. Only platform admins (Clerk
 * `publicMetadata.platform_admin: true`) get past `RequirePlatformAdmin`.
 * A tenant who somehow lands on admin.autoflo.app and signs in sees
 * the forbidden card; the Worker also returns 403 on /api/admin/* so
 * no data leaks.
 *
 * Surfaces:
 *   /                 → Tenants directory
 *   /tenants/new      → Curated onboarding wizard
 *   /tenants/:id      → Per-tenant detail
 *   /usage            → AI usage analytics
 *   /integrations     → Platform integrations status board
 *   /audit            → Platform-admin audit log
 */
function AdminShellRoutes() {
  return (
    <Routes>
      <Route element={<AdminLayout />}>
        <Route
          element={
            <RequirePlatformAdmin>
              <Outlet />
            </RequirePlatformAdmin>
          }
        >
          <Route path="/" element={<AdminTenants />} />
          <Route path="/tenants/new" element={<AdminNewTenant />} />
          <Route path="/tenants/:id" element={<AdminTenantDetail />} />
          <Route path="/usage" element={<AdminUsage />} />
          <Route path="/usage/:shopId" element={<AdminUsageDetail />} />
          <Route path="/eric-activity" element={<AdminEricActivity />} />
          <Route path="/activity" element={<AdminActivity />} />
          <Route path="/alerts" element={<AdminAlerts />} />
          <Route path="/integrations" element={<AdminIntegrations />} />
          <Route path="/audit" element={<AdminAuditLog />} />
          <Route path="*" element={<AdminTenants />} />
        </Route>
      </Route>
    </Routes>
  )
}

/**
 * Renders Clerk's `<OrganizationList hidePersonal />` when the signed-in
 * user has no active organization, otherwise hands off to the routed
 * app shell. Personal accounts are disabled in the Clerk dashboard, so
 * a user reaching this state is one who's been invited but hasn't
 * accepted, or who needs to switch into an org. The `<OrganizationList />`
 * UI handles both flows.
 *
 * The Worker's auth middleware mirrors this gate server-side — any
 * /api/* call without an `org_id` claim returns 401 `no_active_organization`.
 * Keeping the client gate prevents needless API failures and shows a
 * useful Clerk-rendered UI instead of an error envelope.
 *
 * `Create organization` is hidden in the OrganizationList for non-
 * platform-admins. Tenant shop owners + staff should join an org via
 * invitation, never spin up new ones themselves. Mira/Emil still see
 * the Create button so they can onboard new tenants.
 */
function OrgGate({ children }: { children: React.ReactNode }) {
  const { t: tAuth } = useTranslation('auth')
  const { t: tCommon } = useTranslation('common')
  const { organization, isLoaded } = useOrganization()
  const { user } = useUser()
  const { getToken } = useAuth()
  const isPlatformAdmin = useIsPlatformAdmin()

  // Resolve shop_id once per (user, org) so PostHog identify has a
  // tenant key to attach. /api/me is the canonical mapping from
  // clerk_org_id → shop_id; reading it once and caching with TanStack
  // (staleTime: Infinity) avoids per-event refetches and keeps the
  // session consistent across the rest of the app.
  const meQuery = useQuery<{ shop_id: string; role: string | null }>({
    queryKey: ['me', user?.id, organization?.id],
    enabled: !!user?.id && !!organization?.id,
    staleTime: Infinity,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error('no_token')
      const res = await fetch('/api/me', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('me_failed')
      return (await res.json()) as { shop_id: string; role: string | null }
    },
  })

  // Identify once the (user, org, /api/me result) triple is ready.
  // shop_id is the group key (PostHog group analytics); clerk_user_id
  // is the person distinctId. See docs/tracking-plan.md §2.
  useEffect(() => {
    const me = meQuery.data
    if (!me || !user || !organization) return
    identifyShopUser({
      clerkUserId: user.id,
      shopId: me.shop_id,
      orgRole: me.role,
      orgName: organization.name ?? null,
    })
  }, [meQuery.data, user, organization])

  // Org-switch context leak (plan §6): when the user changes orgs via
  // <OrganizationSwitcher>, Clerk re-issues the JWT but PostHog still
  // holds the previous shop as a super-property. Reset on org-id change
  // so the next identify effect attaches to the new shop. Skip on
  // first mount — we don't want to reset before we've ever identified.
  const prevOrgIdRef = useRef<string | null>(null)
  useEffect(() => {
    const currentId = organization?.id ?? null
    if (prevOrgIdRef.current && prevOrgIdRef.current !== currentId) {
      resetIdentity()
    }
    prevOrgIdRef.current = currentId
  }, [organization?.id])

  if (!isLoaded) {
    return (
      <div className="min-h-dvh grid place-items-center bg-background">
        <div className="text-sm text-muted-foreground">{tCommon('loading')}</div>
      </div>
    )
  }

  if (!organization) {
    return (
      <div className="min-h-dvh grid place-items-center bg-background px-4 py-8">
        <div className="w-full max-w-md space-y-4">
          <div className="space-y-1 text-center">
            <h1 className="text-xl font-bold text-foreground">
              {tAuth('org_select.title')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {tAuth('org_select.description')}
            </p>
          </div>
          <OrganizationList
            hidePersonal
            appearance={
              isPlatformAdmin
                ? undefined
                : {
                    elements: {
                      organizationListCreateOrganizationActionButton: {
                        display: 'none',
                      },
                    },
                  }
            }
          />
        </div>
      </div>
    )
  }

  return <>{children}</>
}

/**
 * Owner gate for routes that should be invisible to mechanics /
 * receptionists. Today, only `/settings` mounts this — all other routes
 * are open to any reader. The Worker enforces the same gate
 * (`requireRole(...OWNER_ROLES)`) on every PATCH/POST under /api/shop,
 * /api/dealers, /api/labor-matrix, so a non-owner can't bypass via
 * the dev console.
 */
function RequireOwner({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('settings')
  const canManage = useCanManageIntegrations()
  if (!canManage) {
    return (
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="text-base font-semibold text-foreground">
          {t('forbidden.title')}
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('forbidden.body')}
        </p>
      </div>
    )
  }
  return <>{children}</>
}

/**
 * Gates a route to platform admins (autoflo operators — Mira, Emil —
 * whose Clerk `publicMetadata.platform_admin: true`). Distinct from
 * `RequireOwner` (org-level role): platform-admin spans tenants and
 * guards the admin.autoflo.app surface, NOT tenant resources.
 *
 * The Worker enforces the same gate on /api/admin/* via
 * `requirePlatformAdmin()`, so a tenant who somehow lands here can't
 * bypass.
 */
function RequirePlatformAdmin({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('admin')
  const isPlatformAdmin = useIsPlatformAdmin()
  if (!isPlatformAdmin) {
    return (
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="text-base font-semibold text-foreground">
          {t('forbidden.title')}
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('forbidden.body')}
        </p>
      </div>
    )
  }
  return <>{children}</>
}

function Logo() {
  const { t } = useTranslation('nav')
  return (
    <>
      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-[14px] bg-brand text-white font-bold">
        A
      </div>
      <div className="leading-tight">
        {/* "autoflo" is the product brand name — kept literal, not
            translated. Same rule as "GYM Auto" in mock data. */}
        <div className="text-lg font-bold text-brand tracking-tight">autoflo</div>
        <div className="text-xs text-muted-foreground">
          {isAdminHost ? t('admin_subtitle') : t('logo_subtitle')}
        </div>
      </div>
    </>
  )
}

function NavList({
  orientation,
  onNavigate,
}: {
  orientation: 'horizontal' | 'vertical'
  onNavigate?: () => void
}) {
  const { t } = useTranslation()
  const horizontal = orientation === 'horizontal'
  const location = useLocation()
  const links = useNavLinks()
  return (
    <nav
      className={
        horizontal
          ? 'hidden lg:flex items-center gap-1'
          : 'flex flex-col gap-1'
      }
    >
      {links.map((l) => {
        const active = location.pathname === l.to
        const base = horizontal
          ? 'rounded-md px-3 py-1.5 text-sm transition'
          // Vertical (sheet) links: comfortable touch targets — at least 44px tall.
          : 'flex min-h-11 items-center rounded-md px-3 text-sm transition'
        const variant = active
          ? 'bg-muted text-foreground hover:bg-muted'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        return (
          <Link
            key={l.to}
            to={l.to}
            onClick={onNavigate}
            className={`${base} ${variant}`}
          >
            {t(l.labelKey)}
          </Link>
        )
      })}
    </nav>
  )
}

function AdminLayout() {
  const { t } = useTranslation('nav')
  const { t: tWhatsNew } = useTranslation('whats_new')
  const [navOpen, setNavOpen] = useState(false)
  // Force-open state for the WhatsNewDialog. The dialog still auto-pops
  // on first visit for unseen releases regardless of this — `forceOpen`
  // is the manual override the trigger button uses to re-surface the
  // dialog after dismissal.
  const [whatsNewOpen, setWhatsNewOpen] = useState(false)
  const closeNav = () => setNavOpen(false)
  const location = useLocation()
  const lastTrackedPageRef = useRef<string | null>(null)
  const isPlatformAdmin = useIsPlatformAdmin()

  // Hide the "Create organization" action in OrganizationSwitcher for
  // non-platform-admins. Tenant shop owners + staff should never
  // spawn new orgs themselves — onboarding flows through curated
  // invitations from Mira/Emil. Platform admins keep the create option
  // because they need it to onboard new tenants from admin.autoflo.app.
  //
  // The CSS hide is a soft gate; the durable enforcement is the Clerk
  // dashboard setting "Members can create organizations" — flip that
  // to OFF for full server-side enforcement.
  const orgSwitcherAppearance = isPlatformAdmin
    ? undefined
    : {
        elements: {
          organizationSwitcherPopoverActionButton__createOrganization: {
            display: 'none',
          },
        },
      }

  // page.viewed fires on actual navigation only, not on re-renders.
  // Dedupe via ref against the last tracked page name. The route name
  // (not the full pathname) is what we send — pathnames contain IDs.
  useEffect(() => {
    const page = pageNameFor(location.pathname, isAdminHost)
    if (lastTrackedPageRef.current === page) return
    lastTrackedPageRef.current = page
    track('page.viewed', { page })
  }, [location.pathname])

  return (
    <div className="min-h-dvh bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-background">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3 sm:px-6">
          {/* Hamburger — visible below lg, opens the side drawer. */}
          <Sheet open={navOpen} onOpenChange={setNavOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('open_menu')}
                className="lg:hidden"
              >
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            {/* Override the base SheetContent (`flex flex-col gap-4 p-6`
                with no overflow handling) so the body scrolls when the
                10 nav links + org switcher + "What's new" tip the drawer
                taller than the viewport. Pin the header at the top,
                make the rest of the content a scrollable middle column.
                `100dvh` so the iOS URL-bar shrink/expand doesn't clip
                the tail of the drawer. */}
            <SheetContent
              side="left"
              className="w-72 gap-0 overflow-hidden p-0 sm:w-80"
            >
              <SheetHeader className="shrink-0 px-6 pt-6">
                <SheetTitle asChild>
                  <Link
                    to="/"
                    onClick={closeNav}
                    className="flex items-center gap-3"
                  >
                    <Logo />
                  </Link>
                </SheetTitle>
              </SheetHeader>
              <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
                <div className="mt-6">
                  <NavList orientation="vertical" onNavigate={closeNav} />
                </div>
                {/* Org switcher in the drawer for mobile. The desktop
                    switcher (in the header below) is hidden at this
                    breakpoint, so the drawer is the only path for
                    switching orgs on phones. */}
                <div className="mt-6 border-t border-border pt-4">
                  <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('organization_label')}
                  </div>
                  <OrganizationSwitcher
                    hidePersonal
                    appearance={orgSwitcherAppearance}
                  />
                </div>

                {/* What's new — re-open the dismissed release dialog.
                    Lives in the drawer on mobile (the desktop trigger is
                    in the header below). */}
                <div className="mt-4 border-t border-border pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setWhatsNewOpen(true)
                      closeNav()
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm text-foreground hover:bg-muted"
                  >
                    <Sparkles className="size-4 text-brand" />
                    {tWhatsNew('open_label')}
                  </button>
                </div>
              </div>
            </SheetContent>
          </Sheet>

          {/* Logo + title — visible at all breakpoints. The subtitle hides on
              very narrow screens to keep the header compact. */}
          <Link to="/" className="flex shrink-0 items-center gap-3">
            <Logo />
          </Link>

          {/* Desktop horizontal nav — pushed right by ml-auto, only at lg+. */}
          <div className="ml-auto hidden lg:block">
            <NavList orientation="horizontal" />
          </div>

          {/* Org switcher — visible at lg+ only. On mobile it lives in
              the drawer (above) to keep the header from crowding. */}
          <div className="ml-auto hidden lg:block">
            <OrganizationSwitcher
              hidePersonal
              appearance={orgSwitcherAppearance}
            />
          </div>

          {/* What's new — desktop-only trigger (mobile lives in the
              drawer above). Small Sparkles icon button to keep the
              header from crowding; tooltip via aria-label + title. */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={tWhatsNew('open_label')}
            title={tWhatsNew('open_label')}
            onClick={() => setWhatsNewOpen(true)}
            className="hidden lg:inline-flex"
          >
            <Sparkles className="size-4 text-brand" />
          </Button>

          {/* Eric-online dot — sits just before the UserButton on the
              tenant host. Mounted in the header so it's available
              site-wide (not just on the dashboard) without each page
              re-rendering its own. Hidden on the admin host where
              the assistant isn't part of the surface. */}
          {!isAdminHost ? (
            <div className="ml-auto lg:ml-0 flex items-center">
              <EricOnlineDot />
            </div>
          ) : null}

          {/* Clerk user menu — top-right at all breakpoints. On mobile,
              ml-auto pushes it right when the Eric dot isn't there
              (admin host). With the dot present, `ml-2` keeps a tight
              gap between the dot and the avatar. */}
          <div className={isAdminHost ? 'ml-auto lg:ml-0' : 'ml-2'}>
            <UserButton />
          </div>
        </div>
      </header>

      {!isAdminHost ? <ProductionTenantBanner /> : null}

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
        <Outlet />
      </main>

      {/* Mounted at the layout level so the trigger from any page (and
          the drawer / header buttons above) can re-surface it. The
          dialog still auto-opens on first visit for unseen releases. */}
      <WhatsNewDialog
        forceOpen={whatsNewOpen}
        onOpenChange={(next) => setWhatsNewOpen(next)}
      />
    </div>
  )
}
